import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Copy, Download, LogOut, BarChart3, FileText, Settings } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import ReactDiffViewer from 'react-diff-viewer-continued';
import { RewriteResponse } from '@/types/api';
import SEOHead from '@/components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

interface ModeOption {
  value: string;
  label: string;
}

interface ToneOption {
  value: string;
  label: string;
}

export default function EditorPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { user, logout, fetchUserData } = useAuth();
  const [originalText, setOriginalText] = useState<string>('');
  const [rewrittenText, setRewrittenText] = useState<string>('');
  const [mode, setMode] = useState<string>('standard');
  const [tone, setTone] = useState<string>('professional');
  const [loading, setLoading] = useState<boolean>(false);
  const [plagiarismScore, setPlagiarismScore] = useState<number | null>(null);
  const [showDiff, setShowDiff] = useState<boolean>(false);
  const [currentHistoryId, setCurrentHistoryId] = useState<string | null>(null);

  const modes: ModeOption[] = [
    { value: 'light', label: 'Light' },
    { value: 'standard', label: 'Standard' },
    { value: 'aggressive', label: 'Aggressive' },
    { value: 'human-like', label: 'Human-Like' }
  ];

  const tones: ToneOption[] = [
    { value: 'academic', label: 'Academic' },
    { value: 'professional', label: 'Professional' },
    { value: 'casual', label: 'Casual' },
    { value: 'creative', label: 'Creative' },
    { value: 'formal', label: 'Formal' }
  ];

  const handleRewrite = async (): Promise<void> => {
    if (!originalText.trim()) {
      toast.error('Please enter text to rewrite');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post<RewriteResponse>(`${API}/rewrite`, {
        text: originalText,
        mode,
        tone
      });

      setRewrittenText(response.data.rewritten_text);
      setPlagiarismScore(response.data.plagiarism_percentage);
      setCurrentHistoryId(response.data.id);
      toast.success('Text rewritten successfully!');
      
      await fetchUserData();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to rewrite text';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (): Promise<void> => {
    if (!rewrittenText) {
      toast.error('No text to copy');
      return;
    }

    try {
      await navigator.clipboard.writeText(rewrittenText);
      toast.success('Copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleDownload = (): void => {
    if (!rewrittenText) {
      toast.error('No text to download');
      return;
    }

    const blob = new Blob([rewrittenText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rewritten-text.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Downloaded as .txt!');
  };

  const handleDownloadDocx = async (): Promise<void> => {
    if (!currentHistoryId) {
      toast.error('No text to download');
      return;
    }

    try {
      const response = await axios.get(`${API}/download/${currentHistoryId}/docx`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rewritten-text-${currentHistoryId.substring(0, 8)}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Downloaded as .docx!');
    } catch (error: any) {
      toast.error('Failed to download .docx file');
    }
  };

  const wordCount = (text: string): number => {
    return text.trim() ? text.trim().split(/\s+/).length : 0;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title="Text Rewriter Editor"
        description="Rewrite your text using AI. Choose from multiple modes and tones."
        path="/editor"
        noindex={true}
      />
      
      {/* Email Verification Banner - Disabled */}
      
      {/* Header */}
      <header className="backdrop-blur-xl bg-white/80 border-b border-white/20 supports-[backdrop-filter]:bg-white/60 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-indigo-600 to-indigo-400 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <span className="text-lg sm:text-2xl font-jakarta font-bold text-slate-900">PlagiFree AI</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="text-xs sm:text-sm text-slate-600">
                <span className="font-semibold text-indigo-600">{(user?.daily_limit || 20) - (user?.rewrites_today || 0)}</span>
                <span className="hidden sm:inline"> / {user?.daily_limit || 20}</span> left
              </div>
              {user?.is_admin && (
                <button
                  data-testid="admin-setup-btn"
                  onClick={() => navigate('/admin/setup')}
                  className="hidden md:flex items-center gap-2 bg-violet-100 text-violet-700 hover:bg-violet-200 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium transition-colors"
                >
                  <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Owner Setup</span>
                </button>
              )}
              <button
                data-testid="dashboard-btn"
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-1 sm:gap-2 bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium transition-colors"
              >
                <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Dashboard</span>
              </button>
              <button
                data-testid="logout-btn"
                onClick={() => {
                  logout();
                  navigate('/');
                  toast.success('Logged out successfully');
                }}
                className="flex items-center gap-1 sm:gap-2 text-slate-600 hover:text-slate-900 text-xs sm:text-sm font-medium transition-colors"
              >
                <LogOut className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Editor */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Mode & Tone Selectors */}
        <div className="mb-4 sm:mb-6 space-y-3 sm:space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-semibold text-slate-700 mb-2">Rewrite Mode</label>
            <div className="flex flex-wrap gap-2">
              {modes.map((m) => (
                <button
                  key={m.value}
                  data-testid={`mode-${m.value}-btn`}
                  onClick={() => setMode(m.value)}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${
                    mode === m.value
                      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/30'
                      : 'bg-white text-slate-700 border border-slate-200 hover:border-indigo-300'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-semibold text-slate-700 mb-2">Tone</label>
            <div className="flex flex-wrap gap-2">
              {tones.map((t) => (
                <button
                  key={t.value}
                  data-testid={`tone-${t.value}-btn`}
                  onClick={() => setTone(t.value)}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${
                    tone === t.value
                      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/30'
                      : 'bg-white text-slate-700 border border-slate-200 hover:border-indigo-300'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Two-Panel Editor */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 min-h-[500px] sm:min-h-[600px]">
          {/* Original Text Panel */}
          <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 flex flex-col h-[400px] sm:h-full overflow-hidden">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-100 flex justify-between items-center gap-2">
              <h3 className="font-jakarta font-semibold text-slate-900 text-sm sm:text-base">Original Text</h3>
              <div className="text-xs sm:text-sm text-slate-500">
                <span data-testid="original-word-count">{wordCount(originalText)}</span> words
              </div>
            </div>
            <textarea
              data-testid="original-text-input"
              value={originalText}
              onChange={(e) => setOriginalText(e.target.value)}
              placeholder="Paste your text here..."
              className="editor-textarea flex-1 w-full resize-none border-none focus:ring-0 p-6 text-lg font-source bg-transparent outline-none placeholder:text-slate-300"
            />
          </div>

          {/* Rewritten Text Panel */}
          <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 flex flex-col h-[400px] sm:h-full overflow-hidden">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-100">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-jakarta font-semibold text-slate-900 text-sm sm:text-base">Plagiarism-Free Text</h3>
                <div className="flex items-center gap-1 sm:gap-2">
                  {plagiarismScore !== null && (
                    <div className="flex items-center gap-1 bg-emerald-50 px-2 sm:px-3 py-1 rounded-full">
                      <span className="text-xs font-semibold text-emerald-700">{plagiarismScore}% Unique</span>
                    </div>
                  )}
                  {rewrittenText && (
                    <button
                      onClick={() => setShowDiff(!showDiff)}
                      className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 sm:px-3 py-1 rounded-full font-medium transition-colors"
                      data-testid="toggle-diff-btn"
                    >
                      {showDiff ? 'Hide' : 'Show'}
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="text-xs sm:text-sm text-slate-500">
                  <span data-testid="rewritten-word-count">{wordCount(rewrittenText)}</span> words
                </div>
                <button
                  data-testid="copy-btn"
                  onClick={handleCopy}
                  disabled={!rewrittenText}
                  className="p-1.5 sm:p-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Copy to clipboard"
                >
                  <Copy className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
                <button
                  data-testid="download-txt-btn"
                  onClick={handleDownload}
                  disabled={!rewrittenText}
                  className="p-1.5 sm:p-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Download as .txt"
                >
                  <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
                <button
                  data-testid="download-docx-btn"
                  onClick={handleDownloadDocx}
                  disabled={!currentHistoryId}
                  className="p-1.5 sm:p-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Download as .docx"
                >
                  <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 p-6 text-lg font-source text-slate-800 overflow-y-auto editor-textarea">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-slate-600">Rewriting your text...</p>
                  </div>
                </div>
              ) : rewrittenText ? (
                showDiff ? (
                  <div className="text-sm">
                    <ReactDiffViewer
                      oldValue={originalText}
                      newValue={rewrittenText}
                      splitView={false}
                      showDiffOnly={false}
                      useDarkTheme={false}
                      styles={{
                        variables: {
                          light: {
                            diffViewerBackground: '#ffffff',
                            addedBackground: '#d4f8d4',
                            removedBackground: '#ffd4d4'
                          }
                        }
                      }}
                    />
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    data-testid="rewritten-text-output"
                  >
                    {rewrittenText}
                  </motion.div>
                )
              ) : (
                <p className="text-slate-300 text-center h-full flex items-center justify-center">
                  Your rewritten text will appear here...
                </p>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Sticky Rewrite Button */}
      <AnimatePresence>
        {originalText.trim() && !loading && (
          <motion.button
            data-testid="rewrite-sticky-btn"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            onClick={handleRewrite}
            className="fixed bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2 z-50 shadow-2xl shadow-indigo-500/40 bg-indigo-600 text-white rounded-full px-6 sm:px-10 py-3 sm:py-4 text-base sm:text-lg font-bold flex items-center gap-2 hover:scale-105 transition-transform"
          >
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
            Rewrite Text
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}